import { useMantineTheme } from '@mantine/core';
import upperFirst from 'lodash/upperFirst';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useAppSelector } from '@/app/hooks';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { HelpPopover } from '@/components/HelpPopover/HelpPopover';
import { analysisView } from '@/app/analysis';
import {
  getSpanNetworkColors,
  getNetworkColors,
  getCategoryTitle,
  getCategoryThresholds,
  getSpanLegendValues,
} from './utils';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import { useNetworkGroupLabel } from '@/helpers/networkGroups';
import { useHasGroupRiskSummary } from '@/hooks/useWildfireMode';
import { useHasFuelsWithoutWildfirePriority } from '@/hooks/useHasFuelsWithoutWildfirePriority';
import { formatWildfireCurrency } from '@/helpers/wildfire';

import classes from './NetworkLegend.module.css';

export const NetworkLegend = () => {
  const theme = useMantineTheme();
  const isSmallScreen = useSmallScreen();
  const {
    selectedAnalysis,
    selectedNetworkGroup,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    spanNetworkGroup,
    activeGroupName,
    mapNetworkGroupId,
  } = useAppSelector((state) => state.analysisMap);

  const isSpan =
    mapNetworkGroupId === spanNetworkGroup?.id || !!activeGroupName;

  const networkGroupLabel = useNetworkGroupLabel(
    isSpan ? spanNetworkGroup : selectedNetworkGroup,
    'pluralTitle'
  );
  const hasFuelsWithoutWildfirePriority = useHasFuelsWithoutWildfirePriority();
  const legendColors = isSpan
    ? getSpanNetworkColors(
        selectedAnalysis,
        theme,
        hasFuelsWithoutWildfirePriority
      )
    : getNetworkColors(selectedAnalysis, theme);
  const { formatMessage } = useClientTerminology();
  const hasGroupRiskSummary = useHasGroupRiskSummary();
  const legendTitle = getCategoryTitle(
    selectedAnalysis,
    formatMessage,
    isSpan,
    hasGroupRiskSummary,
    hasFuelsWithoutWildfirePriority
  );
  const groupLegendValues = getCategoryThresholds(
    selectedAnalysis,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds
  );

  const spanLegendValues = getSpanLegendValues(
    selectedAnalysis,
    criticalTreesThresholds,
    hasFuelsWithoutWildfirePriority
  );

  const legendValues = isSpan ? spanLegendValues : groupLegendValues;
  const isWildfireDollars =
    selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
    hasGroupRiskSummary;
  const legend = (
    <>
      {legendValues.map((v: number | string, idx: number) => {
        const isLast = idx === legendValues.length - 1;
        const isNumeric = typeof v === 'number';
        const formatted =
          isWildfireDollars && isNumeric ? formatWildfireCurrency(v) : v;
        return (
          <span key={idx}>{`${formatted}${
            isLast && isNumeric ? '+' : ''
          }`}</span>
        );
      })}
    </>
  );

  const helpUrl = analysisView[selectedAnalysis as GroupAnalysisType].helpLink;
  return (
    selectedNetworkGroup && (
      <div>
        {!isSmallScreen && (
          <div className={classes.title}>
            <span>{`${networkGroupLabel} by ${legendTitle?.toLocaleLowerCase()}`}</span>
            {helpUrl && (
              <HelpPopover
                link={helpUrl}
                linkText={
                  analysisView[selectedAnalysis as GroupAnalysisType]
                    .helpShortText ?? upperFirst(selectedAnalysis)
                }
                fontSize="sm"
              ></HelpPopover>
            )}
          </div>
        )}
        <div
          className={classes.body}
          style={{
            gridTemplateColumns: `${'1fr '.repeat(legendValues.length).trim()}`,
          }}
        >
          {legendColors?.map((c: string, idx: number) => {
            const height = 8;
            return (
              <span
                key={idx}
                style={{
                  backgroundColor: c,
                  height,
                  borderTopLeftRadius: idx === 0 ? height : 0,
                  borderBottomLeftRadius: idx === 0 ? height : 0,
                  borderTopRightRadius:
                    idx === legendValues.length - 1 ? height : 0,
                  borderBottomRightRadius:
                    idx === legendValues.length - 1 ? height : 0,
                }}
              />
            );
          })}
          {legend}
        </div>
      </div>
    )
  );
};
